import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { PanelLeft } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { RagNavRefreshProvider } from "@/apps/rag/hooks/RagNavRefreshContext";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { UploadStatusText } from "@/components/form/InstructionWordLimit";
import { Sidebar } from "@/components/layout/Sidebar";
import { KmBreadcrumbWorkspaceSwitcher } from "@/components/layout/KmBreadcrumbWorkspaceSwitcher";
import { useSummarizeJobList } from "@/lib/summarize-jobs";
import { cn } from "@/lib/utils";
import { useLocalStorage } from "@/hooks/useLocalStorage";

const SIDEBAR_COLLAPSED_KEY = "doculegis-sidebar-collapsed";

export function DashboardLayout() {
  const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>(
    SIDEBAR_COLLAPSED_KEY,
    false
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileNavOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <RagNavRefreshProvider>
        <div className="relative flex h-[100dvh] min-h-screen overflow-hidden bg-transparent text-[#101828] dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-800 dark:text-white">
          {mobileNavOpen ? (
            <button
              type="button"
              className="absolute inset-0 z-40 bg-[rgba(15,23,42,0.35)] lg:hidden dark:bg-black/40"
              aria-label="Close navigation"
              onClick={() => setMobileNavOpen(false)}
            />
          ) : null}
          <Sidebar
            isCollapsed={isCollapsed}
            mobileOpen={mobileNavOpen}
            onMobileNavClose={() => setMobileNavOpen(false)}
          />
          <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
            <header className="flex h-12 shrink-0 items-center gap-2 border-b px-3 border-[rgba(15,23,42,0.09)] bg-[var(--bar-bg)] backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/50">
              <div className="flex min-w-0 shrink-0 items-center gap-1.5">
                <div className="lg:hidden">
                  <IconButton
                    type="button"
                    onClick={() => setMobileNavOpen(true)}
                    title="Open navigation"
                    aria-label="Open navigation"
                    className="rounded-lg border-transparent bg-[var(--nav-active-bg)] text-[#7c3aed] hover:border-transparent hover:bg-[rgba(147,51,245,0.15)] hover:text-[#6d28d9] dark:bg-indigo-500/20 dark:text-violet-300 dark:hover:bg-indigo-500/30 dark:hover:text-violet-200"
                  >
                    <PanelLeft strokeWidth={1.75} />
                  </IconButton>
                </div>
                <div className="hidden lg:block">
                  <IconButton
                    type="button"
                    active={isCollapsed}
                    onClick={() => setIsCollapsed((v) => !v)}
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    className="border-transparent hover:border-transparent dark:border-transparent dark:hover:border-transparent"
                  >
                    <PanelLeft
                      className={cn(
                        "transition-transform duration-200",
                        isCollapsed && "scale-x-[-1]"
                      )}
                      strokeWidth={1.75}
                    />
                  </IconButton>
                </div>
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <KmBreadcrumbWorkspaceSwitcher />
              </div>
              <div className="shrink-0">
                <ThemeToggle />
              </div>
            </header>
            <main className="relative flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
              <BackgroundUploadStatus />
              <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
                <Outlet />
              </div>
            </main>
          </div>
        </div>
    </RagNavRefreshProvider>
  );
}

function BackgroundUploadStatus() {
  const jobs = useSummarizeJobList();
  if (jobs.length === 0) return null;
  return (
    <div className="flex shrink-0 flex-wrap gap-x-4 gap-y-1 border-b border-[rgba(15,23,42,0.09)] bg-[var(--surface-sunken)] px-4 py-2 dark:border-white/10 dark:bg-slate-900/40">
      {jobs.map((job) => (
        <span key={job.key} className="inline-flex min-w-0 items-center gap-2 text-xs text-[var(--lm-foreground)] dark:text-slate-200">
          <span className="max-w-[280px] truncate" title={job.fileName}>
            {job.fileName}
          </span>
          <UploadStatusText status={job.status === "ready" ? "ready" : "not-ready"} />
        </span>
      ))}
    </div>
  );
}
